import {Paper} from "@mantine/core";
import {useCallback} from "react";
import {Col, Row} from "../../components/Flex/Flex";
import api from "../../utils/api";
import {useQuery} from "@tanstack/react-query";
import {DataTable} from "mantine-datatable";
import {AreaChart} from "@mantine/charts";
import { WarningCircle as IconAlertTriangle } from "@phosphor-icons/react";
import Page from "../../components/Page/Page.tsx";


interface AdminStatistics {
  bunny: {
    available: boolean;
    totalBandwidth: number;
    totalRequests: number;
    cacheHitRate: number;
    bandwidthChart: Record<string, number>;
    originTraffic: number;
  };
  storage: {
    totalStorage: number;
    totalFiles: number;
    topUsers: Array<{
      _id: string;
      totalStorage: number;
      fileCount: number;
    }>;
    disabledFiles: number;
  };
  optimization: {
    totalBytesSaved: number;
    totalOriginalSize: number;
    totalOptimizedSize: number;
    averageSavingsPercentage: number;
    savingsByFormat: Array<{
      extension: string;
      count: number;
      totalOriginal: number;
      totalOptimized: number;
      totalSaved: number;
      percentage: number;
    }>;
  };
}


export default function AdminFileDashboard() {
  const {data: users} = useQuery({
    queryKey: ['users', 'public'],
    queryFn: async () => {
      const {data} = await api.get('/users');
      return data;
    },
  })

  // Fetch statistics
  const {data: stats, isLoading: statsLoading} = useQuery<AdminStatistics>({
    queryKey: ['admin-file-stats'],
    queryFn: async () => {
      const {data} = await api.get('/admin/files/statistics');
      return data;
    },
  });
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
    return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
  };

  const bodyUser = useCallback((rowData: any) => {
    const user = users?.find((u: any) => u.uid === rowData._id);
    return (user?.handle);
  }, [users]);

  // Prepare bandwidth chart data
  const getBandwidthChartData = () => {
    if (!stats?.bunny.bandwidthChart) return null;

    return Object.entries(stats.bunny.bandwidthChart).map(([date, bytes]) => ({
      date: new Date(date).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}),
      bandwidth: bytes / (1024 * 1024), // Convert to MB
    }));
  };

  const bandwidthSeries = [{name: 'bandwidth', label: 'Bandwidth (MB)', color: 'blue.5'}];

  return (
    <Page>
      <title>Admin - File Hosting</title>
      <Row align="center" className="mb-4">
        <Col>
          <h1>File Hosting Administration</h1>
        </Col>
      </Row>

      {statsLoading ? (
        <p>Loading statistics...</p>
      ) : stats && (
        <>
          {/* Bunny.net CDN Statistics */}
          <h2>CDN Statistics (Bunny.net)</h2>
          {stats.bunny?.available ? (
            <>
              <Row className="mb-4 gap-3">
                <Col>
                  <Paper>
                    <h3 className="mt-0">Total Bandwidth</h3>
                    <h2>{formatFileSize(stats.bunny.totalBandwidth)}</h2>
                    <small className="text-color-secondary">Last 30 days</small>
                  </Paper>
                </Col>
                <Col>
                  <Paper>
                    <h3 className="mt-0">Total Requests</h3>
                    <h2>{stats.bunny.totalRequests.toLocaleString()}</h2>
                    <small className="text-color-secondary">Last 30 days</small>
                  </Paper>
                </Col>
                <Col>
                  <Paper>
                    <h3 className="mt-0">Cache Hit Rate</h3>
                    <h2>{stats.bunny.cacheHitRate.toFixed(1)}%</h2>
                    <small className="text-color-secondary">Efficiency metric</small>
                  </Paper>
                </Col>
                <Col>
                  <Paper>
                    <h3 className="mt-0">Origin Traffic</h3>
                    <h2>{formatFileSize(stats.bunny.originTraffic)}</h2>
                    <small className="text-color-secondary">From storage</small>
                  </Paper>
                </Col>
              </Row>

              <Paper className="mb-4">
                <h3 className="mt-0">Bandwidth Usage (Last 30 Days)</h3>
                <div>
                  {getBandwidthChartData() && (
                    <AreaChart
                      h={300}
                      data={getBandwidthChartData()!}
                      dataKey="date"
                      series={bandwidthSeries}
                      curveType="natural"
                      withDots={false}
                      valueFormatter={(v) => `${v.toFixed(1)} MB`}
                    />
                  )}
                </div>
              </Paper>
            </>
          ) : (
            <Paper className="mb-4">
              <p className="text-color-secondary">
                <IconAlertTriangle size={16} style={{marginRight: '0.5rem', display: 'inline'}}/>
                Bunny.net statistics are not available. Check API configuration.
              </p>
            </Paper>
          )}

          {/* Database Storage Statistics */}
          <h2>Storage Statistics (Database)</h2>
          <Row className="mb-4 gap-3">
            <Col>
              <Paper>
                <h3 className="mt-0">Total Storage Used</h3>
                <h2>{formatFileSize(stats.storage.totalStorage)}</h2>
              </Paper>
            </Col>
            <Col>
              <Paper>
                <h3 className="mt-0">Total Files</h3>
                <h2>{stats.storage.totalFiles.toLocaleString()}</h2>
              </Paper>
            </Col>
            <Col>
              <Paper>
                <h3 className="mt-0">Disabled Files</h3>
                <h2>{stats.storage.disabledFiles}</h2>
              </Paper>
            </Col>
            <Col>
              <Paper>
                <h3 className="mt-0">Avg File Size</h3>
                <h2>
                  {stats.storage.totalFiles > 0
                    ? formatFileSize(stats.storage.totalStorage / stats.storage.totalFiles)
                    : '0 B'}
                </h2>
              </Paper>
            </Col>
          </Row>

          <Paper className="mb-4">
            <h3 className="mt-0">Top Users by Storage</h3>
            {stats.storage.topUsers.length > 0 ? (
              <DataTable
                records={stats.storage.topUsers}
                idAccessor="_id"
                columns={[
                  {accessor: '_id', title: 'User ID'},
                  {accessor: 'user', title: 'User', render: (row) => bodyUser(row)},
                  {accessor: 'totalStorage', title: 'Storage Used', render: (row) => formatFileSize(row.totalStorage)},
                  {accessor: 'fileCount', title: 'File Count'},
                ]}
              />
            ) : (
              <p className="text-color-secondary">No files uploaded yet</p>
            )}
          </Paper>

          {/* Optimization Statistics */}
          {stats.optimization && stats.optimization.totalOriginalSize > 0 && (
            <>
              <h2>Image Optimization Statistics</h2>
              <Row className="mb-4 gap-3">
                <Col>
                  <Paper>
                    <h3 className="mt-0">Total Savings</h3>
                    <h2>{formatFileSize(stats.optimization.totalBytesSaved)}</h2>
                    <small className="text-color-secondary">
                      {stats.optimization.averageSavingsPercentage.toFixed(1)}% average reduction
                    </small>
                  </Paper>
                </Col>
                <Col>
                  <Paper>
                    <h3 className="mt-0">Original Size</h3>
                    <h2>{formatFileSize(stats.optimization.totalOriginalSize)}</h2>
                    <small className="text-color-secondary">Before optimization</small>
                  </Paper>
                </Col>
                <Col>
                  <Paper>
                    <h3 className="mt-0">Optimized Size</h3>
                    <h2>{formatFileSize(stats.optimization.totalOptimizedSize)}</h2>
                    <small className="text-color-secondary">After optimization</small>
                  </Paper>
                </Col>
                <Col>
                  <Paper>
                    <h3 className="mt-0">Bandwidth Saved</h3>
                    <h2>{formatFileSize(stats.optimization.totalBytesSaved)}</h2>
                    <small className="text-color-secondary">Per download cycle</small>
                  </Paper>
                </Col>
              </Row>

              {stats.optimization.savingsByFormat && stats.optimization.savingsByFormat.length > 0 && (
                <Paper className="mb-4">
                  <h3 className="mt-0">Savings by Original Format</h3>
                  <DataTable
                    records={stats.optimization.savingsByFormat}
                    idAccessor="extension"
                    columns={[
                      {accessor: 'extension', title: 'Format', render: (row) => row.extension?.toUpperCase() || 'Unknown'},
                      {accessor: 'count', title: 'Files'},
                      {accessor: 'totalOriginal', title: 'Original Size', render: (row) => formatFileSize(row.totalOriginal)},
                      {accessor: 'totalOptimized', title: 'Optimized Size', render: (row) => formatFileSize(row.totalOptimized)},
                      {accessor: 'totalSaved', title: 'Saved', render: (row) => formatFileSize(row.totalSaved)},
                      {accessor: 'percentage', title: 'Reduction', render: (row) => (
                        <span>
                          <strong>{row.percentage.toFixed(1)}%</strong>
                        </span>
                      )},
                    ]}
                  />
                </Paper>
              )}
            </>
          )}
        </>
      )}
    </Page>
  );
}
